/**
 * Utility functions for timezone conversion between local time and UTC
 */

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * Converts a local datetime string to UTC ISO string.
 * @param localDateTimeString - Local datetime, e.g., "YYYY-MM-DDTHH:mm" or "YYYY-MM-DD HH:mm"
 * @returns UTC ISO string "YYYY-MM-DDTHH:mm:ss.sssZ"
 */
export function convertLocalToUTC(localDateTimeString: string): string {
  if (!localDateTimeString) throw new Error('Local datetime string is required');

  // Normalize format
  const normalized = localDateTimeString.replace(' ', 'T');
  const localDate = new Date(normalized);

  if (isNaN(localDate.getTime())) throw new Error(`Invalid datetime: ${localDateTimeString}`);

  return localDate.toISOString();
}

/**
 * Converts a UTC ISO string or Date to a local datetime string (YYYY-MM-DDTHH:mm).
 * @param utcISOString - UTC ISO string or Date object
 * @returns Local datetime string "YYYY-MM-DDTHH:mm"
 */
export function convertUTCToLocal(utcISOString: string | Date): string {
  if (!utcISOString) throw new Error('UTC ISO string is required');

  const utcDate = typeof utcISOString === 'string' ? new Date(utcISOString) : utcISOString;

  if (isNaN(utcDate.getTime())) throw new Error('Invalid UTC ISO string or Date');

  return `${utcDate.getFullYear()}-${pad(utcDate.getMonth() + 1)}-${pad(utcDate.getDate())}T${pad(utcDate.getHours())}:${pad(utcDate.getMinutes())}`;
}

/**
 * Converts a local date and time to a UTC ISO string.
 * @param date - "YYYY-MM-DD"
 * @param time - "HH:mm" or "YYYY-MM-DDTHH:mm"
 * @returns UTC ISO string "YYYY-MM-DDTHH:mm:ss.sssZ"
 */
export function convertLocalDateTimeToUTC(date: string, time: string): string {
  if (!date) throw new Error('Date is required');
  if (!time) throw new Error('Time is required');

  // If time contains 'T', treat it as a datetime string
  const dateTime = time.includes('T') ? time : `${date}T${time}`;
  return convertLocalToUTC(dateTime);
}

/**
 * Extracts local date and time from a UTC ISO string or Date object.
 * @param utcISOString - UTC ISO string or Date
 * @returns { date, time }
 */
export function extractLocalDateTimeFromUTC(utcISOString: string | Date): {
  date: string;
  time: string;
} {
  if (!utcISOString) throw new Error('UTC ISO string is required');

  const utcDate = typeof utcISOString === 'string' ? new Date(utcISOString) : utcISOString;
  if (isNaN(utcDate.getTime())) throw new Error('Invalid UTC ISO string or Date');

  const year = utcDate.getFullYear();
  const month = pad(utcDate.getMonth() + 1);
  const day = pad(utcDate.getDate());
  const hours = pad(utcDate.getHours());
  const minutes = pad(utcDate.getMinutes());

  return {
    date: `${year}-${month}-${day}`,
    time: `${year}-${month}-${day}T${hours}:${minutes}`,
  };
}
